import type { FilterPreset } from '@/types/utilisation';

const PRESETS_STORAGE_KEY = 'utilisation-report-presets';
const MAX_PRESETS = 20;

// Default presets
export const DEFAULT_PRESETS: FilterPreset[] = [
  {
    id: 'current-month-all',
    name: 'Current Month - All Employees',
    filters: {
      dateMode: 'preset',
      selectedRange: 'thisMonth',
      selectedDepartment: 'all',
      selectedEmployees: [],
      selectedGroupBy: 'employee',
    },
    createdAt: new Date(),
    isDefault: true,
  },
  {
    id: 'last-quarter-all',
    name: 'Last Quarter - All Departments',
    filters: {
      dateMode: 'preset',
      selectedRange: 'last90days',
      selectedDepartment: 'all',
      selectedEmployees: [],
      selectedGroupBy: 'department',
    },
    createdAt: new Date(),
    isDefault: true,
  },
  {
    id: 'last-month-projects',
    name: 'Last Month - By Project',
    filters: {
      dateMode: 'preset',
      selectedRange: 'lastMonth',
      selectedDepartment: 'all',
      selectedEmployees: [],
      selectedGroupBy: 'project',
    },
    createdAt: new Date(),
    isDefault: true,
  },
];

/**
 * Load all presets from localStorage
 */
export const loadPresets = (): FilterPreset[] => {
  try {
    const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
    if (!stored) {
      return DEFAULT_PRESETS;
    }

    const parsed = JSON.parse(stored) as FilterPreset[];
    
    // Convert date strings back to Date objects
    const presets = parsed.map((preset) => ({
      ...preset,
      createdAt: new Date(preset.createdAt),
      filters: {
        ...preset.filters,
        customDateRange: preset.filters.customDateRange
          ? {
              start: preset.filters.customDateRange.start ? new Date(preset.filters.customDateRange.start) : undefined,
              end: preset.filters.customDateRange.end ? new Date(preset.filters.customDateRange.end) : undefined,
            }
          : undefined,
      },
    }));

    // Merge with default presets (in case new defaults were added)
    const userPresets = presets.filter((p) => !p.isDefault);
    return [...DEFAULT_PRESETS, ...userPresets];
  } catch (error) {
    console.error('Failed to load presets:', error);
    return DEFAULT_PRESETS;
  }
};

/**
 * Save a new preset
 */
export const savePreset = (preset: FilterPreset): { success: boolean; error?: string } => {
  try {
    const presets = loadPresets();
    const userPresets = presets.filter((p) => !p.isDefault);

    // Check if we've reached the limit
    if (userPresets.length >= MAX_PRESETS) {
      return {
        success: false,
        error: `Maximum of ${MAX_PRESETS} presets allowed. Please delete some presets first.`,
      };
    }

    // Check for duplicate names
    if (presets.some((p) => p.name === preset.name && p.id !== preset.id)) {
      return {
        success: false,
        error: 'A preset with this name already exists.',
      };
    }

    // Add the new preset
    const updatedPresets = [...userPresets, preset];
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updatedPresets));

    return { success: true };
  } catch (error) {
    console.error('Failed to save preset:', error);
    return {
      success: false,
      error: 'Failed to save preset. Storage may be full.',
    };
  }
};

/**
 * Delete a preset
 */
export const deletePreset = (id: string): { success: boolean; error?: string } => {
  try {
    const presets = loadPresets();
    const preset = presets.find((p) => p.id === id);

    // Don't allow deleting default presets
    if (preset?.isDefault) {
      return {
        success: false,
        error: 'Cannot delete default presets.',
      };
    }

    const userPresets = presets.filter((p) => !p.isDefault && p.id !== id);
    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(userPresets));

    return { success: true };
  } catch (error) {
    console.error('Failed to delete preset:', error);
    return {
      success: false,
      error: 'Failed to delete preset.',
    };
  }
};

/**
 * Update an existing preset
 */
export const updatePreset = (
  id: string,
  updates: Partial<FilterPreset>
): { success: boolean; error?: string } => {
  try {
    const presets = loadPresets();
    const preset = presets.find((p) => p.id === id);

    // Don't allow updating default presets
    if (preset?.isDefault) {
      return {
        success: false,
        error: 'Cannot update default presets.',
      };
    }

    // Check for duplicate names if name is being updated
    if (updates.name && presets.some((p) => p.name === updates.name && p.id !== id)) {
      return {
        success: false,
        error: 'A preset with this name already exists.',
      };
    }

    const userPresets = presets
      .filter((p) => !p.isDefault)
      .map((p) => (p.id === id ? { ...p, ...updates } : p));

    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(userPresets));

    return { success: true };
  } catch (error) {
    console.error('Failed to update preset:', error);
    return {
      success: false,
      error: 'Failed to update preset.',
    };
  }
};

/**
 * Export presets as JSON
 */
export const exportPresets = (): string => {
  const presets = loadPresets().filter((p) => !p.isDefault);
  return JSON.stringify(presets, null, 2);
};

/**
 * Import presets from JSON
 */
export const importPresets = (
  data: string
): { success: boolean; imported: number; error?: string } => {
  try {
    const imported = JSON.parse(data) as FilterPreset[];

    if (!Array.isArray(imported)) {
      return {
        success: false,
        imported: 0,
        error: 'Invalid preset format.',
      };
    }

    const presets = loadPresets();
    const userPresets = presets.filter((p) => !p.isDefault);

    // Add imported presets, avoiding duplicates
    let importedCount = 0;
    imported.forEach((preset) => {
      if (
        !userPresets.some((p) => p.name === preset.name) &&
        userPresets.length + importedCount < MAX_PRESETS
      ) {
        userPresets.push({
          ...preset,
          id: `imported-${Date.now()}-${importedCount}`,
          createdAt: new Date(),
          isDefault: false,
        });
        importedCount++;
      }
    });

    localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(userPresets));

    return {
      success: true,
      imported: importedCount,
    };
  } catch (error) {
    console.error('Failed to import presets:', error);
    return {
      success: false,
      imported: 0,
      error: 'Failed to parse preset data.',
    };
  }
};

/**
 * Generate a unique preset ID
 */
export const generatePresetId = (): string => {
  return `preset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};
